'use client';

import pickAnswerInQuestionnaireTrigger from '@coolblue-development/ga4-events/triggers/ProductTradeInOverlay/pickAnswerInQuestionnaireTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { createContext, Dispatch, type ReactNode, useMemo, useReducer } from 'react';

import { filterOptions } from '../helpers';
import type { Action, State, StepArgument } from '../types';
import { Actions } from '../types';
import { stateReducer } from './stateReducer';

type ProductTradeInContextValue = State & {
  dispatch?: Dispatch<Action>;
  allEnhancedQuestionsAnswered?: boolean;
};

const initialState: ProductTradeInContextValue = {
  fetched: false,
  isModalOpen: false,
  isLoading: false,
  steps: [],
  currentStep: 0,
  variantChoices: [],
  answers: {},
  entranceChoice: false,
  isExplanationModalOpen: false,
  enhancedAnswers: new Map(),
  currentEnhancedQuestion: undefined,
  answeredEnhancedQuestionsIds: new Set(),
  allEnhancedQuestionsAnswered: false
};

const ProductTradeInContext = createContext<ProductTradeInContextValue>(initialState);

const ProductTradeInContextProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(stateReducer, initialState);

  const { trackEvent } = useAnalytics();

  const {
    entry,
    selectedBrand,
    selectedVariant,
    answers,
    enhancedQuestions,
    answeredEnhancedQuestionsIds
  } = state;

  const filteredOptions = filterOptions(entry?.options, selectedBrand);
  const brands = entry?.optionsBrands;
  const questions = brands && selectedBrand ? brands?.[selectedBrand]?.questions : undefined;

  const allEnhancedQuestionsAnswered = useMemo(() => {
    return (
      enhancedQuestions &&
      enhancedQuestions.length > 0 &&
      answeredEnhancedQuestionsIds &&
      enhancedQuestions.every(question => answeredEnhancedQuestionsIds.has(question.id))
    );
  }, [enhancedQuestions, answeredEnhancedQuestionsIds]);

  const hasSingleVariant =
    state.variantChoices?.length === 1 &&
    filteredOptions?.filter(
      option =>
        option.variances.length === 1 && option.variances[0].value === state.variantChoices?.[0]
    ).length === 1;

  const steps = [
    {
      completed: brands && selectedBrand !== undefined
    },
    {
      completed:
        (selectedVariant !== undefined && selectedVariant !== null) ||
        (selectedVariant === null && hasSingleVariant)
    },
    {
      completed: questions?.map(question => answers[question.questionId]).every(Boolean)
    },
    {
      completed: false
    }
  ];

  function goToStep(stepToSet: StepArgument) {
    if (typeof state.currentStep === 'undefined') return;

    if (stepToSet === 'next' && state.currentStep < 3) {
      dispatch({ type: Actions.SET_CURRENT_STEP, payload: state.currentStep + 1 });
    } else if (stepToSet === 'previous' && state.currentStep > 0) {
      dispatch({ type: Actions.SET_CURRENT_STEP, payload: state.currentStep - 1 });
    } else if (typeof stepToSet === 'number') {
      dispatch({ type: Actions.SET_CURRENT_STEP, payload: stepToSet });
    }
  }

  function updateSelectedVariant(optionId: number | null) {
    dispatch({ type: Actions.SET_SELECTED_VARIANT, payload: optionId });
    dispatch({ type: Actions.SET_ANSWERS, payload: {} });
  }

  const settings = state.selectedBrand
    ? entry?.settingsBrands?.[state.selectedBrand]?.settings
    : undefined;

  const handleAnswerChange: State['handleAnswerChange'] = (question, answer) => {
    trackEvent(
      pickAnswerInQuestionnaireTrigger({
        question: question.question,
        answer,
        question_group: ''
      })
    );
  };

  return (
    <ProductTradeInContext.Provider
      value={{
        ...state,
        steps,
        goToStep,
        brands,
        options: filteredOptions,
        updateSelectedVariant,
        questions,
        settings,
        dispatch,
        brandData: state.entry?.brandData,
        handleAnswerChange,
        allEnhancedQuestionsAnswered
      }}
    >
      {children}
    </ProductTradeInContext.Provider>
  );
};

export { ProductTradeInContext, ProductTradeInContextProvider };
